import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';
import { parseMermaidChartData, looksLikeChart } from './parseMermaidChartData';
import MermaidDiagram from './MermaidDiagram';
import ChartStreamingIndicator from './ChartStreamingIndicator';

// Pastels matching MermaidDiagram - distinct colors for chart segments
const CHART_COLORS = [
  '#4F81E1', '#E15F4F', '#4FBF7F', '#F5A623', '#9B59B6', '#95A5A6',
  '#F5C8BD', '#E9BACC', '#83C4B4', '#A9D194', '#FAEEAE', '#F5BE6B',
];

/**
 * Renders interactive Chart.js charts (pie, bar, line) when mermaid code is a chart.
 * Toggle between Interactive (Chart.js with hover tooltips) and Static (Mermaid SVG).
 * During streaming, shows "Generating chart" indicator instead of glitchy partial render.
 */
const InteractiveChart = ({ children, darkMode = false, chatTitle, onNodeClick, onEdgeClick, isStreaming = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [mode, setMode] = useState('interactive'); // 'interactive' | 'static'
  const diagramCode = typeof children === 'string'
    ? children
    : (Array.isArray(children) ? children.join('') : String(children || '')).trim();

  const chartData = parseMermaidChartData(diagramCode);
  const isChartStreaming = isStreaming && looksLikeChart(diagramCode);

  useEffect(() => {
    if (!chartData || !canvasRef.current || mode !== 'interactive') return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const isDark = !!darkMode;
    const textColor = isDark ? '#e2e8f0' : '#1e293b';
    const gridColor = isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.3)';

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const fontFamily = typeof document !== 'undefined'
      ? (getComputedStyle(document.documentElement).getPropertyValue('--chat-font-family').trim() || 'system-ui, -apple-system, sans-serif')
      : 'system-ui, -apple-system, sans-serif';

    const commonOptions = {
      responsive: true,
      maintainAspectRatio: true,
      animation: false, // Prevent re-animation glitches on re-renders
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: textColor, padding: 16, font: { size: 13, family: fontFamily } },
        },
        tooltip: {
          enabled: true,
          backgroundColor: isDark ? '#1e293b' : '#0f172a',
          titleColor: '#fff',
          bodyColor: '#e2e8f0',
          padding: 12,
          cornerRadius: 8,
        },
      },
    };

    if (chartData.type === 'pie') {
      const total = chartData.values.reduce((a, b) => a + b, 0);
      chartRef.current = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: chartData.labels,
          datasets: [{
            data: chartData.values,
            backgroundColor: CHART_COLORS.slice(0, chartData.labels.length),
            borderWidth: 2,
            borderColor: isDark ? '#0f172a' : '#fff',
          }],
        },
        options: {
          ...commonOptions,
          plugins: {
            ...commonOptions.plugins,
            tooltip: {
              ...commonOptions.plugins.tooltip,
              callbacks: {
                label: (ctx) => {
                  const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : '0';
                  return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
                },
              },
            },
          },
        },
      });
    } else if (chartData.type === 'bar' || chartData.type === 'line') {
      const dsList = chartData.datasets || [{ label: chartData.title || 'Value', values: chartData.values }];
      const colors = CHART_COLORS;
      const chartDatasets = dsList.map((ds, i) => {
        const color = colors[i % colors.length];
        const rgb = color.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
        const rgbaFill = rgb ? `rgba(${parseInt(rgb[1], 16)},${parseInt(rgb[2], 16)},${parseInt(rgb[3], 16)},0.12)` : 'rgba(0,0,0,0.06)';
        if (chartData.type === 'line') {
          return {
            label: ds.label,
            data: ds.values,
            borderColor: color,
            backgroundColor: rgbaFill,
            borderWidth: 3,
            pointRadius: 5,
            pointBackgroundColor: color,
            fill: true,
            tension: 0.4,
          };
        }
        return {
          label: ds.label,
          data: ds.values,
          backgroundColor: color,
          borderWidth: 0,
        };
      });
      chartRef.current = new Chart(ctx, {
        type: chartData.type,
        data: {
          labels: chartData.labels,
          datasets: chartDatasets,
        },
        options: {
          ...commonOptions,
          scales: {
            x: {
              ticks: { color: textColor, maxRotation: 45, font: { family: fontFamily } },
              grid: { color: gridColor },
            },
            y: {
              ticks: { color: textColor, font: { family: fontFamily } },
              grid: { color: gridColor },
            },
          },
          plugins: {
            ...commonOptions.plugins,
            tooltip: {
              ...commonOptions.plugins.tooltip,
              callbacks: {
                label: (ctx) => {
                  const val = ctx.parsed?.y ?? ctx.raw;
                  return ` ${ctx.dataset.label}: ${typeof val === 'number' ? val.toLocaleString() : val}`;
                },
              },
            },
          },
        },
      });
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData, darkMode, mode]);

  // Streaming chart - show indicator, don't render glitchy partial chart
  if (isChartStreaming) {
    const streamingChartType = /^\s*pie\b/i.test(diagramCode) ? 'pie' : /\b(?:xychart-beta|xychart)\b/i.test(diagramCode) ? 'bar' : 'chart';
    return (
      <div className="interactive-chart-wrapper my-2 flex justify-center">
        <ChartStreamingIndicator isDark={darkMode} chartType={streamingChartType} />
      </div>
    );
  }

  // Not a chart - use MermaidDiagram
  if (!chartData) {
    return (
      <MermaidDiagram darkMode={darkMode} chatTitle={chatTitle} onNodeClick={onNodeClick} onEdgeClick={onEdgeClick}>
        {children}
      </MermaidDiagram>
    );
  }

  const buttonBaseClass = 'rounded-md px-2.5 py-1.5 text-sm transition-colors hover:bg-[#e0e0e0] dark:hover:bg-[#222222]';
  const buttonActiveClass = 'bg-[#e5e7eb] dark:bg-[#374151] text-gray-900 dark:text-white font-medium';
  const buttonInactiveClass = 'text-gray-500 dark:text-gray-400';

  return (
    <div className="interactive-chart-wrapper my-2 w-full relative max-w-full">
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <button
          type="button"
          onClick={() => setMode('interactive')}
          className={`${buttonBaseClass} ${mode === 'interactive' ? buttonActiveClass : buttonInactiveClass}`}
          aria-pressed={mode === 'interactive'}
        >
          Interactive
        </button>
        <button
          type="button"
          onClick={() => setMode('static')}
          className={`${buttonBaseClass} ${mode === 'static' ? buttonActiveClass : buttonInactiveClass}`}
          aria-pressed={mode === 'static'}
        >
          Static
        </button>
      </div>

      {mode === 'interactive' ? (
        <div className="interactive-chart-container rounded-2xl overflow-hidden bg-white dark:bg-slate-900 p-5 pt-12 shadow-sm w-full mx-auto" style={{ minHeight: 360 }}>
          {chartData.title && (
            <h3 className="text-center font-semibold mb-4 text-gray-800 dark:text-gray-200" style={{ fontSize: '1rem', fontFamily: 'var(--chat-font-family)' }}>
              {chartData.title}
            </h3>
          )}
          <div className="flex justify-center items-center w-full" style={{ position: 'relative', height: chartData.type === 'pie' ? 340 : 320 }}>
            <canvas ref={canvasRef} className="max-w-full" style={{ maxHeight: chartData.type === 'pie' ? 320 : 320 }} />
          </div>
        </div>
      ) : (
        <div className="interactive-chart-static-wrapper rounded-2xl overflow-hidden bg-white dark:bg-slate-900 p-5 pt-12 shadow-sm w-full mx-auto min-h-[360px] flex items-center justify-center">
          <div className="interactive-chart-static-mermaid w-full min-w-[600px] min-h-[320px] flex items-center justify-center">
            <MermaidDiagram darkMode={darkMode} chatTitle={chatTitle} onNodeClick={onNodeClick} onEdgeClick={onEdgeClick}>
              {children}
            </MermaidDiagram>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveChart;
